<template>
  <v-card>
    <v-progress-linear
      :active="loading"
      :indeterminate="loading"
      absolute
      location="top"
    ></v-progress-linear>

    <v-data-table
      class="mt-2"
      :no-data-text="$t('reports.dataTable.noResultsText')"
      :headers="headers"
      :items="fileList"
      item-value="key"
      :class="{ disabled: loading }"
      :sort-by="[{ key: 'modified', order: 'desc' }]"
      :show-expand="showExpand"
      :expanded="expanded"
      @update:expanded="value => (expanded = value.slice(-1))"
    >
      <template v-slot:expanded-row="{ columns, item }">
        <tr>
          <td :colspan="columns.length" class="pa-0">
            <ParameterListView :machine="machine" :paramFile="item" />
          </td>
        </tr>
      </template>

      <template v-slot:[`item.modified`]="{ value }">
        <span class="grab-downloads-item-date">{{ value }}</span>
      </template>

      <template v-slot:[`item.icon`]="{ item }">
        <v-tooltip location="top" v-if="canDownload">
          <template v-slot:activator="{ props }">
            <v-icon v-bind="props" @click="downloadItem(item)">{{
              item.icon
            }}</v-icon>
          </template>
          <span>{{ $t("common.actions.download") }}</span>
        </v-tooltip>
      </template>

      <template v-slot:[`item.action`]="{ item }">
        <v-progress-circular
          v-if="item.downloading"
          :size="20"
          :width="2"
          indeterminate
        />

        <template v-else-if="$vuetify.display['xs']">
          <v-btn v-if="canDownload" @click="downloadItem(item)" variant="flat" size="small">
            {{ $t("common.actions.download") }}
          </v-btn>
        </template>

        <v-menu v-else location="start" scroll-strategy="none">
          <template v-slot:activator="{ props: menu }">
            <v-btn
              icon
              variant="text"
              color="primary"
              v-bind="menu"
              class="grab-downloads-action-menu-btn"
            >
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item v-if="canDownload" @click="downloadItem(item)">
              <v-list-item-title class="text-body-2">
                <v-icon size="small">mdi-download</v-icon>
                {{ $t("common.actions.download") }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import fetchClient from "@/utils/fetchClient"
import formatter from "@/utils/formatter"

import ParameterListView from "./ParameterListView.vue"

export default {
  props: ["machine"],
  components: {
    ParameterListView
  },
  data: () => ({
    files: [],
    loading: false,
    expanded: []
  }),
  created() {
    this.fetchFileList()
  },
  computed: {
    ...mapGetters(["canReadDownloads", "isAdmin", "isService", "isProduction"]),

    canDownload() {
      return this.canReadDownloads || this.isAdmin
    },
    showExpand() {
      return this.isProduction
    },
    headers() {
      let hdrs = []

      if (this.canDownload) {
        hdrs.push({
          title: "",
          key: "icon",
          hide: "xs",
          width: "56px",
          sortable: false
        })
      }

      hdrs.push({
        title: this.$t("reports.dataTable.headers.name"),
        key: "name"
      })
      hdrs.push({ title: this.$t("downloads.size"), key: "size" })
      hdrs.push({
        title: this.$t("reports.dataTable.headers.modified"),
        key: "modified"
      })

      if (this.canDownload) {
        hdrs.push({
          title: this.$t("common.dataTable.headers.actions"),
          key: "action",
          align: "center",
          sortable: false
        })
      }

      return hdrs.filter(h => !h.hide || !this.$vuetify.display[h.hide])
    },

    fileList() {
      return this.files.map(file => {
        return {
          icon: "mdi-archive-arrow-down-outline",
          key: file.key,
          name: file.name,
          size: `${Math.floor(file.size / 1024)} kb`,
          modified: this.formatDate(file.modified),
          downloading: false
        }
      })
    }
  },
  methods: {
    ...mapActions(["setError"]),

    async fetchFileList() {
      const url = `/api/v1/tenants/${this.machine.tenantUid}/machines/${this.machine.serialNo}/parameterFiles`
      this.loading = true

      try {
        const response = await fetchClient().getDataOrThrow(url)
        this.files = response.files
      } catch (error) {
        this.setError(error)
      } finally {
        this.loading = false
      }
    },
    async downloadItem(file) {
      try {
        const url = `/api/v1/tenants/${this.machine.tenantUid}/machines/${this.machine.serialNo}/parameterFiles/${file.name}`
        const blob = await fetchClient().getBlobOrThorw(url)

        let link = document.createElement("a")
        link.href = window.URL.createObjectURL(blob)
        link.download = file.name
        link.click()
      } catch (error) {
        this.setError(error)
      }
    },
    formatDate(date) {
      if (this.isProduction) {
        if (!(date instanceof Date)) {
          date = new Date(date)
        }
        return date.toISOString()
      }

      return formatter().formatDate(date)
    }
  }
}
</script>

<style scoped></style>
